// src/modules/leads/pages/LeadImport.jsx
// Import leads from CSV / Excel (SheetJS) or a published Google Sheet CSV URL,
// with column mapping, preview and auto round-robin assignment.
import React, { useEffect, useState } from 'react';
import {
  Card, Upload, Button, Table, Select, Space, Typography, Row, Col, message,
  Divider, Input, Switch, Alert, Result, Segmented,
} from 'antd';
import { InboxOutlined, ImportOutlined, LinkOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { fetchBranches } from '../../branches/api';
import { usePermissions } from '../../../hooks/usePermissions';
import { importLeads, fetchLeadTypes } from '../api';
import { IMPORT_FIELDS } from '../constants';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

// Guess which source column maps to a target field, by fuzzy header match.
const SYNONYMS = {
  name: ['name', 'customer', 'client', 'full name', 'lead name'],
  phone: ['phone', 'mobile', 'contact', 'number', 'cell', 'mob'],
  altPhone: ['alt', 'alternate', 'phone2', 'secondary'],
  email: ['email', 'mail', 'e-mail'],
  address: ['address', 'addr', 'location'],
  city: ['city', 'town'],
  state: ['state'],
  pincode: ['pincode', 'pin', 'zip', 'postal'],
  leadType: ['type', 'category', 'product', 'interest'],
};
const guessColumn = (field, columns) => {
  const syns = SYNONYMS[field] || [field];
  const found = columns.find((c) => syns.some((s) => c.toLowerCase().includes(s)));
  return found || null;
};

export default function LeadImport() {
  const { can } = usePermissions();
  const isHO = can('leadManagement', 'view') || can('branches', 'view'); // heuristic: allow branch pick to privileged users

  const [mode, setMode] = useState('FILE'); // FILE | SHEET
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [parsing, setParsing] = useState(false);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadType, setLeadType] = useState(null);
  const [autoAssign, setAutoAssign] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {});
    fetchLeadTypes().then((r) => setLeadTypes(r?.data?.data || [])).catch(() => {});
  }, []);

  const ingest = (workbookRows, name, source) => {
    if (!workbookRows.length) { message.warning('No rows found in the file'); return; }
    const cols = Object.keys(workbookRows[0]);
    setRows(workbookRows);
    setColumns(cols);
    setFileName(name);
    // Auto-map.
    const auto = {};
    IMPORT_FIELDS.forEach((f) => { const g = guessColumn(f.key, cols); if (g) auto[f.key] = g; });
    setMapping(auto);
    setResult(null);
    message.success(`Parsed ${workbookRows.length} rows from ${source}`);
  };

  const parseArrayBuffer = (data, name) => {
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
    ingest(json, name, 'file');
  };

  const beforeUpload = (file) => {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try { parseArrayBuffer(e.target.result, file.name); }
      catch (err) { message.error('Could not parse file: ' + err.message); }
      finally { setParsing(false); }
    };
    reader.onerror = () => { setParsing(false); message.error('Could not read file'); };
    reader.readAsArrayBuffer(file);
    return false; // prevent auto-upload
  };

  const loadSheet = async () => {
    if (!sheetUrl.trim()) { message.warning('Paste a published Google Sheet CSV link'); return; }
    setParsing(true);
    try {
      const res = await fetch(sheetUrl.trim());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const wb = XLSX.read(text, { type: 'string' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      ingest(json, 'Google Sheet', 'Google Sheet');
    } catch (err) {
      message.error('Could not fetch the sheet (make sure it is “Published to web” as CSV). ' + err.message);
    } finally { setParsing(false); }
  };

  const setMap = (field, col) => setMapping((m) => ({ ...m, [field]: col || undefined }));

  const previewRows = rows.slice(0, 10).map((r, i) => {
    const out = { key: i };
    IMPORT_FIELDS.forEach((f) => { out[f.key] = mapping[f.key] ? r[mapping[f.key]] : ''; });
    return out;
  });

  const canImport = rows.length && mapping.name && mapping.phone;

  const doImport = async () => {
    setImporting(true);
    try {
      const res = await importLeads({
        rows, mapping, fileName,
        branch: branchId || undefined,
        leadType: leadType || undefined,
        source: mode === 'SHEET' ? 'GOOGLE_SHEET' : (/\.xlsx?$/.test(fileName) ? 'EXCEL_IMPORT' : 'CSV_IMPORT'),
        autoAssign,
      });
      setResult(res.data.data);
      message.success(res.data.message);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Import failed');
    } finally { setImporting(false); }
  };

  if (result) {
    const r = result.result || {};
    return (
      <Result
        status="success"
        title="Import complete"
        subTitle={`Imported ${r.imported} · Duplicates ${r.duplicates} · Failed ${r.failed}${result.assignment ? ` · Auto-assigned ${result.assignment.assigned}` : ''}`}
        extra={[
          <Button type="primary" key="again" onClick={() => { setResult(null); setRows([]); setColumns([]); setMapping({}); setFileName(''); setSheetUrl(''); }}>Import another</Button>,
        ]}
      >
        {r.errors?.length ? (
          <Alert type="warning" showIcon message={`${r.errors.length} row(s) had issues`}
            description={<ul style={{ margin: 0 }}>{r.errors.slice(0, 8).map((e, i) => <li key={i}>{e.error}</li>)}</ul>} />
        ) : null}
      </Result>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <Title level={4}><ImportOutlined /> Import Leads</Title>
      <Paragraph type="secondary">Upload a CSV/Excel of leads (at least Name & Phone), map the columns, preview, then import. You can auto-distribute to CSRs.</Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Segmented value={mode} onChange={setMode} options={[{ label: 'CSV / Excel file', value: 'FILE' }, { label: 'Google Sheet (CSV link)', value: 'SHEET' }]} style={{ marginBottom: 12 }} />
        {mode === 'FILE' ? (
          <Dragger accept=".csv,.xlsx,.xls" beforeUpload={beforeUpload} showUploadList={false} disabled={parsing}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag a CSV / Excel file here</p>
            <p className="ant-upload-hint">First row must be column headers (e.g. Name, Phone).</p>
          </Dragger>
        ) : (
          <Space.Compact style={{ width: '100%' }}>
            <Input prefix={<LinkOutlined />} placeholder="https://docs.google.com/…/pub?output=csv" value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} />
            <Button type="primary" loading={parsing} onClick={loadSheet}>Load</Button>
          </Space.Compact>
        )}
        {mode === 'SHEET' && (
          <Alert style={{ marginTop: 8 }} type="info" showIcon
            message="In Google Sheets: File → Share → Publish to web → choose the sheet → CSV, then paste that link here." />
        )}
      </Card>

      {rows.length > 0 && (
        <>
          <Card size="small" title={`Map Columns — ${fileName} (${rows.length} rows)`} style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]}>
              {IMPORT_FIELDS.map((f) => (
                <Col xs={24} sm={8} key={f.key}>
                  <Text>{f.label}{f.required && <Text type="danger"> *</Text>}</Text>
                  <Select style={{ width: '100%' }} allowClear placeholder="— not mapped —"
                    value={mapping[f.key]} onChange={(v) => setMap(f.key, v)}
                    options={columns.map((c) => ({ value: c, label: c }))} />
                </Col>
              ))}
            </Row>
            {!canImport && <Alert style={{ marginTop: 12 }} type="warning" showIcon message="Name and Phone must be mapped to import." />}
          </Card>

          <Card size="small" title="Import Options" style={{ marginBottom: 16 }}>
            <Space wrap>
              {isHO && (
                <Select allowClear showSearch optionFilterProp="children" style={{ width: 220 }} placeholder="Branch (assign to)" value={branchId} onChange={(v) => setBranchId(v || null)}>
                  {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
                </Select>
              )}
              <Select allowClear style={{ width: 200 }} placeholder="Default lead type" value={leadType} onChange={(v) => setLeadType(v || null)}
                options={leadTypes.map((t) => ({ value: t.code, label: t.name }))} />
              <Space><Switch checked={autoAssign} onChange={setAutoAssign} /><Text>Auto-assign to CSRs (round-robin)</Text></Space>
            </Space>
          </Card>

          <Card size="small" title="Preview (first 10)">
            <Table size="small" dataSource={previewRows} pagination={false} scroll={{ x: true }}
              columns={IMPORT_FIELDS.map((f) => ({ title: f.label, dataIndex: f.key, render: (v) => v || <Text type="secondary">—</Text> }))} />
            <Divider />
            <Button type="primary" size="large" icon={<ImportOutlined />} disabled={!canImport} loading={importing} onClick={doImport}>
              Import {rows.length} leads
            </Button>
          </Card>
        </>
      )}
    </div>
  );
}
